import { rect, aRect, px } from '../../../pixel/canvas';
import { SLOT } from '../slots';
import type { Part } from '../../types';

// Anchor = top of torso (x=32, y~22 for average)
// Tops overdraw the body's skin in the torso region

export const tops: Record<string, Part> = {
  top_tshirt: {
    id: 'top_tshirt', category: 'top', name: 'T-Shirt', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.primary);    // body
      rect(ctx, a.x - 9, a.y + 2, 3, 7, p.primary);   // left sleeve
      rect(ctx, a.x + 6, a.y + 2, 3, 7, p.primary);   // right sleeve
      aRect(ctx, a.x, a.y + 2, 14, 1, p.accentDark);  // shoulder seam
      aRect(ctx, a.x, a.y + 2, 4, 3, p.light);        // collar gap (skin)
    },
  },
  top_hoodie: {
    id: 'top_hoodie', category: 'top', name: 'Hoodie', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 16, 13, p.primary);    // body (wide)
      rect(ctx, a.x - 10, a.y + 2, 4, 9, p.primary);  // left sleeve
      rect(ctx, a.x + 6, a.y + 2, 4, 9, p.primary);   // right sleeve
      // Hood
      aRect(ctx, a.x, a.y, 14, 4, p.primary);
      aRect(ctx, a.x, a.y, 6, 5, p.light);             // hood opening (skin gap)
      // Pocket
      aRect(ctx, a.x, a.y + 10, 8, 4, p.dark);
      aRect(ctx, a.x, a.y + 10, 6, 1, p.accentDark);  // pocket top
    },
  },
  top_tank: {
    id: 'top_tank', category: 'top', name: 'Tank Top', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 10, 13, p.primary);    // narrow body
      // Straps
      rect(ctx, a.x - 3, a.y + 1, 2, 3, p.primary);   // left strap
      rect(ctx, a.x + 1, a.y + 1, 2, 3, p.primary);   // right strap
      aRect(ctx, a.x, a.y + 2, 8, 1, p.accentDark);   // top edge
    },
  },
  top_jacket: {
    id: 'top_jacket', category: 'top', name: 'Jacket', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.primary);
      rect(ctx, a.x - 9, a.y + 2, 3, 10, p.primary);  // left sleeve
      rect(ctx, a.x + 6, a.y + 2, 3, 10, p.primary);  // right sleeve
      // Lapels (V-shape)
      rect(ctx, a.x - 3, a.y + 2, 3, 7, p.dark);      // left lapel
      rect(ctx, a.x, a.y + 2, 3, 7, p.dark);           // right lapel
      // Zipper
      rect(ctx, a.x - 1, a.y + 2, 2, 13, p.metalDark);
      // Collar
      aRect(ctx, a.x, a.y + 1, 8, 2, p.dark);
    },
  },
  top_uniform: {
    id: 'top_uniform', category: 'top', name: 'Uniform', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.dark);
      rect(ctx, a.x - 9, a.y + 2, 3, 10, p.dark);
      rect(ctx, a.x + 6, a.y + 2, 3, 10, p.dark);
      // Collar/lapels
      rect(ctx, a.x - 3, a.y + 2, 3, 5, p.primary);
      rect(ctx, a.x, a.y + 2, 3, 5, p.primary);
      // Buttons
      px(ctx, a.x - 1, a.y + 5, p.metalLight);
      px(ctx, a.x - 1, a.y + 8, p.metalLight);
      px(ctx, a.x - 1, a.y + 11, p.metalLight);
      // Epaulette
      aRect(ctx, a.x, a.y + 2, 14, 2, p.accent);
    },
  },
  top_armor: {
    id: 'top_armor', category: 'top', name: 'Armor', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.metalDark);  // chest plate
      aRect(ctx, a.x, a.y + 2, 12, 2, p.metal);       // top panel
      aRect(ctx, a.x, a.y + 8, 10, 2, p.metal);       // mid panel
      // Shoulder pads
      aRect(ctx, a.x - 9, a.y + 2, 6, 5, p.metal);
      aRect(ctx, a.x + 3, a.y + 2, 6, 5, p.metal);
      // Center gem
      aRect(ctx, a.x, a.y + 5, 4, 3, p.accent);
      aRect(ctx, a.x, a.y + 5, 2, 1, p.accentLight);
    },
  },
  top_stripes: {
    id: 'top_stripes', category: 'top', name: 'Stripes', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.primary);
      rect(ctx, a.x - 9, a.y + 2, 3, 7, p.primary);
      rect(ctx, a.x + 6, a.y + 2, 3, 7, p.primary);
      // Horizontal stripes
      aRect(ctx, a.x, a.y + 4, 14, 2, p.accent);
      aRect(ctx, a.x, a.y + 8, 14, 2, p.accent);
      aRect(ctx, a.x, a.y + 12, 14, 2, p.accent);
      // Collar
      aRect(ctx, a.x, a.y + 2, 4, 2, p.light);
    },
  },
  top_crop: {
    id: 'top_crop', category: 'top', name: 'Crop Top', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 12, 7, p.primary);     // short top
      rect(ctx, a.x - 7, a.y + 2, 2, 5, p.primary);   // left strap
      rect(ctx, a.x + 5, a.y + 2, 2, 5, p.primary);   // right strap
      aRect(ctx, a.x, a.y + 8, 12, 1, p.accentDark);  // bottom edge
      aRect(ctx, a.x, a.y + 2, 12, 1, p.accentDark);  // top edge
    },
  },
  top_robe: {
    id: 'top_robe', category: 'top', name: 'Robe', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 16, 13, p.primary);
      aRect(ctx, a.x, a.y + 15, 18, 20, p.primary);   // skirt portion extends down
      rect(ctx, a.x - 10, a.y + 2, 4, 12, p.primary); // wide left sleeve
      rect(ctx, a.x + 6, a.y + 2, 4, 12, p.primary);  // wide right sleeve
      // Center opening/sash
      rect(ctx, a.x - 1, a.y + 2, 2, 13, p.dark);
      aRect(ctx, a.x, a.y + 18, 6, 6, p.accent);      // sash/belt
      // Hem
      aRect(ctx, a.x, a.y + 34, 18, 1, p.accentDark);
    },
  },
  top_coat: {
    id: 'top_coat', category: 'top', name: 'Long Coat', requires: SLOT.TORSO,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 2, 14, 13, p.dark);
      aRect(ctx, a.x, a.y + 15, 16, 22, p.dark);      // coat skirt
      rect(ctx, a.x - 9, a.y + 2, 3, 12, p.dark);     // left sleeve
      rect(ctx, a.x + 6, a.y + 2, 3, 12, p.dark);     // right sleeve
      // Lapels
      rect(ctx, a.x - 3, a.y + 2, 3, 8, p.primary);
      rect(ctx, a.x, a.y + 2, 3, 8, p.primary);
      // Buttons
      px(ctx, a.x - 1, a.y + 9, p.metal);
      px(ctx, a.x - 1, a.y + 12, p.metal);
      // Collar
      aRect(ctx, a.x, a.y + 1, 10, 2, p.dark);
    },
  },
};
